import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  COUNTRY_OPTIONS,
  countryOptionForIso,
  type CountryCode,
} from "@/utils/phone";

export interface PhoneValue {
  iso: CountryCode;
  /** Digits only, without the dial code. */
  national: string;
}

interface PhoneNumberInputProps {
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Country selector (flag, name, dial code, searchable) plus a digits-only
 * number box — the payment page's phone field rebuilt on the CRM's kit.
 * Popover-based like ProfessionalCombobox so the search input keeps focus.
 */
export function PhoneNumberInput({
  value,
  onChange,
  onBlur,
  placeholder = "8291638739",
  disabled,
  className,
}: PhoneNumberInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = countryOptionForIso(value.iso);

  const options = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (o) =>
        o.name.toLowerCase().includes(query) ||
        o.dialCode.includes(query) ||
        o.iso.toLowerCase() === query,
    );
  }, [search]);

  return (
    <div className={cn("flex gap-x-2", className)}>
      {/* modal: the form lives in a Dialog, which blocks wheel/touch scrolling
          on anything portaled outside it. A modal popover manages its own
          scroll lock, so the country list scrolls. */}
      <Popover
        modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={`Country code, ${selected.name}`}
            disabled={disabled}
            className="h-12 w-[120px] shrink-0 justify-between gap-1.5 bg-white px-2.5 font-normal text-gray-900 shadow-none"
          >
            <Flag option={selected} />
            <span className="truncate text-sm">{selected.dialCode}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search country or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
          </div>
          <div
            role="listbox"
            aria-label="Countries"
            className="max-h-[280px] overflow-y-auto p-1"
          >
            {options.length > 0 ? (
              options.map((option) => {
                const isSelected = option.iso === value.iso;
                return (
                  <button
                    key={option.iso}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className="relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      onChange({ ...value, iso: option.iso });
                      setSearch("");
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "absolute left-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <Flag option={option} />
                    <span className="truncate">{option.name}</span>
                    <span className="ml-auto shrink-0 text-muted-foreground">
                      {option.dialCode}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                No country found
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Input
        type="tel"
        inputMode="numeric"
        placeholder={placeholder}
        disabled={disabled}
        value={value.national}
        onChange={(e) =>
          onChange({ ...value, national: e.target.value.replace(/\s/g, "") })
        }
        onBlur={onBlur}
        className="h-12 flex-1 shadow-none drop-shadow-none"
      />
    </div>
  );
}

function Flag({ option }: { option: { iso: string; name: string; flag: string } }) {
  return (
    <img
      src={option.flag}
      alt=""
      aria-hidden="true"
      width={20}
      height={15}
      loading="lazy"
      className="h-[15px] w-5 shrink-0 rounded-[2px] object-cover"
      onError={(e) => {
        // Flag CDN unavailable: keep the layout, drop the broken image.
        e.currentTarget.style.visibility = "hidden";
      }}
    />
  );
}
